import React, { useMemo, useState } from "react";
import { standardProfiles } from "../../data/bufferStandards";
import ChartLegend from "./ChartLegend";
import {
  standardsVerifiedDate,
  standardsVerifiedLabel,
} from "../../data/defensieStandards";

const clampPercent = (value, maxValue) =>
  Math.max(0, Math.min(100, (value / maxValue) * 100));

const MinimumVsTargetChart = ({
  compact = false,
  defaultProfileId = "mariniers",
  framed = true,
}) => {
  const [activeId, setActiveId] = useState(defaultProfileId);

  const activeProfile = useMemo(
    () =>
      standardProfiles.find((profile) => profile.id === activeId) ||
      standardProfiles[0],
    [activeId]
  );

  const Wrapper = framed ? "div" : "section";
  const wrapperClassName = framed
    ? "mm-card w-full min-w-0 overflow-hidden p-4 md:p-6"
    : "w-full min-w-0";

  return (
    <Wrapper className={wrapperClassName}>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl">
            Minimum vs richtniveau
          </h3>
          <p className="mt-2 text-sm text-mmTextMuted leading-relaxed max-w-2xl">
            De witte markering toont de aanvullende eenheidstest van het
            gekozen profiel. Het gele bereik toont interne Mission Movement
            trainingsdoelen, geen officiële eisen. Dit is geen Cluster 6-tabel.
          </p>
        </div>

        <div
          className="grid grid-cols-2 rounded-xl border border-mmBorder bg-black/20 p-1 text-xs font-semibold uppercase tracking-wider"
          role="tablist"
          aria-label="Kies een eenheid"
        >
          {standardProfiles.map((profile) => {
            const isActive = profile.id === activeId;
            return (
              <button
                key={profile.id}
                type="button"
                onClick={() => setActiveId(profile.id)}
                className={`rounded-lg px-3 py-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-mmAccent/40 ${
                  isActive
                    ? "bg-mmAccent text-black"
                    : "text-mmTextMuted hover:text-mmText"
                }`}
                role="tab"
                aria-selected={isActive}
              >
                {profile.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 space-y-3 text-sm text-mmTextMuted leading-relaxed">
        <p className="font-semibold text-mmText">{activeProfile.standard.title}</p>
        <p>{activeProfile.standard.summary}</p>
        <p>{activeProfile.standard.note}</p>
        <p>
          Bron: <a className="text-mmAccent underline" href={activeProfile.standard.sourceUrl}>{activeProfile.standard.sourceLabel}</a>.
          {" "}Laatst gecontroleerd: <time dateTime={standardsVerifiedDate}>{standardsVerifiedLabel}</time>.
        </p>
      </div>

      <ChartLegend
        items={[
          { label: "Minimum aanvullende eenheidstest", className: "bg-white" },
          { label: "Mission Movement richtniveau", className: "bg-mmAccent" },
        ]}
      />

      <div className="mt-5 space-y-4">
        {activeProfile.rows.map((row) => {
          const minimumPercent = clampPercent(row.minimumValue, row.maxValue);
          const targetStart = clampPercent(row.targetLow, row.maxValue);
          const targetEnd = clampPercent(row.targetHigh, row.maxValue);

          return (
            <div
              key={row.label}
              className="rounded-xl border border-mmBorder bg-black/20 p-4"
            >
              <div className="grid gap-3 md:grid-cols-[minmax(150px,0.7fr)_minmax(0,1fr)] md:items-center">
                <div>
                  <p className="font-display uppercase tracking-widest text-mmText text-base">
                    {row.label}
                  </p>
                  {!compact && (
                    <p className="mt-1 text-xs text-mmTextMuted">
                      Minimum {row.minimumLabel}. Richtniveau{" "}
                      {row.targetLabel}.
                    </p>
                  )}
                </div>

                <div
                  className="space-y-2"
                  aria-label={`${activeProfile.label}, ${row.label}: publieke minimumreferentie ${row.minimumLabel}, Mission Movement richtniveau ${row.targetLabel}`}
                >
                  <div className="flex items-center justify-between gap-3 text-[11px] uppercase tracking-wider text-mmTextMuted">
                    <span>Ondergrens</span>
                    <span>Sterkere buffer</span>
                  </div>

                  <div className="relative h-8 rounded-full border border-mmBorderStrong bg-mmSurface">
                    <div
                      className="absolute top-1/2 h-3 -translate-y-1/2 rounded-full bg-mmAccent"
                      style={{
                        left: `${targetStart}%`,
                        width: `${Math.max(3, targetEnd - targetStart)}%`,
                      }}
                      aria-hidden="true"
                    />
                    <div
                      className="absolute top-1/2 h-7 w-1 -translate-y-1/2 rounded-full bg-white"
                      style={{ left: `${minimumPercent}%` }}
                      aria-hidden="true"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs md:text-sm">
                    <div>
                      <span className="block text-mmTextMuted">
                        Publiek minimum
                      </span>
                      <span className="font-semibold text-mmText">
                        {row.minimumLabel}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="block text-mmTextMuted">
                        Richtniveau voorbereiding
                      </span>
                      <span className="font-semibold text-mmAccent">
                        {row.targetLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-4 border-l-2 border-mmAccent pl-4 text-xs md:text-sm leading-relaxed text-mmTextMuted">
        Deze richtniveaus zijn interne voorbereidingsdoelen. Ze vervangen geen
        officiële eisen en zeggen niets over gegarandeerde selectie. Marsen en
        belasting dragen blijven een afzonderlijke trainingsfocus; ze zijn geen
        extra onderdeel van de hierboven weergegeven Mariniers- of KCT-test.
      </p>
    </Wrapper>
  );
};

export default MinimumVsTargetChart;
